"use client";

import React, { useState } from "react";
import { 
  X, 
  Wine, 
  Users, 
  Calendar, 
  Sparkles, 
  CheckCircle2, 
  AlertCircle,
  Building,
  ArrowRight,
  ShieldCheck
} from "lucide-react";
import { PRIVATE_DINING_SALONS } from "@/lib/demos/restaurantData";

interface PrivateDiningModalProps {
  isOpen: boolean;
  onClose: () => void;
  currency: "INR" | "USD";
  initialSalonId?: string;
  onInquirySubmitted?: (res: any) => void;
}

export default function PrivateDiningModal({
  isOpen,
  onClose,
  currency,
  initialSalonId,
  onInquirySubmitted,
}: PrivateDiningModalProps) {
  const selectedSalonDefault = PRIVATE_DINING_SALONS.find(s => s.id === initialSalonId)?.name || PRIVATE_DINING_SALONS[0].name;

  const [formData, setFormData] = useState({
    guest_name: "",
    email: "",
    phone: "",
    party_size: 10,
    reservation_date: new Date(Date.now() + 86400000 * 7).toISOString().split("T")[0],
    time_slot: "07:30 PM (Private Salon Takeover)",
    dining_area: selectedSalonDefault,
    occasion: "Corporate Dinner & Client Entertainment",
    tasting_menu: "Prestige Truffle & Wagyu Reserve",
    estimated_spend: currency === "INR" ? 85000 : 1100,
    special_requests: "Requires dedicated sommelier flight and customized printed menus with corporate logo.",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submittedInquiry, setSubmittedInquiry] = useState<any>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const res = await fetch("/api/demos/restaurant/reservations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          currency,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to submit private dining inquiry");
      }

      setSubmittedInquiry(data.reservation);
      if (onInquirySubmitted) onInquirySubmitted(data.reservation);
    } catch (err: any) {
      setErrorMessage(err.message || "An unexpected error occurred.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetModal = () => {
    setSubmittedInquiry(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div 
        className="relative w-full max-w-2xl bg-stone-950 border border-amber-500/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800 bg-stone-900/60">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400">
              <Wine className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white tracking-tight">Private Dining & VIP Salons</h3>
              <p className="text-xs text-stone-400">Exclusive Event Inquiries & Sommelier Curations</p>
            </div>
          </div>

          <button
            onClick={handleResetModal}
            className="p-1.5 rounded-lg text-stone-400 hover:text-white hover:bg-stone-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 overflow-y-auto space-y-6">
          {submittedInquiry ? (
            /* Success State */
            <div className="text-center py-6 space-y-5">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 flex items-center justify-center mx-auto animate-bounce">
                <CheckCircle2 className="w-8 h-8" />
              </div>

              <div className="space-y-2">
                <span className="text-xs uppercase tracking-widest font-bold text-amber-400 bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/20">
                  Salon Proposal Dispatched
                </span>
                <h4 className="text-2xl font-bold text-white">Private Salon Intake Received</h4>
                <p className="text-sm text-stone-300 max-w-md mx-auto">
                  Thank you, <strong>{submittedInquiry.guest_name}</strong>. Our Private Events Director will contact you within 2 business hours with floor layouts and sommelier flights.
                </p>
              </div>

              <div className="bg-stone-900/90 border border-stone-800 rounded-xl p-5 text-left max-w-lg mx-auto space-y-3">
                <div className="flex justify-between items-center border-b border-stone-800 pb-2">
                  <span className="text-xs font-semibold text-stone-400">Event Dossier Ref</span>
                  <span className="text-xs font-mono font-bold text-amber-400">#SALON-{submittedInquiry.id}-LA</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-stone-400 block">Designated Salon</span>
                    <span className="text-white font-semibold">{submittedInquiry.dining_area}</span>
                  </div>
                  <div>
                    <span className="text-stone-400 block">Expected Attendance</span>
                    <span className="text-white font-semibold">{submittedInquiry.party_size} Executive Guests</span>
                  </div>
                  <div>
                    <span className="text-stone-400 block">Target Event Date</span>
                    <span className="text-amber-300 font-semibold">{submittedInquiry.reservation_date}</span>
                  </div>
                  <div>
                    <span className="text-stone-400 block">Budget Commitment</span>
                    <span className="text-emerald-400 font-semibold">{currency === "INR" ? `₹${submittedInquiry.estimated_spend.toLocaleString()}` : `$${submittedInquiry.estimated_spend.toLocaleString()}`}</span>
                  </div>
                </div>
              </div>

              <div className="pt-3 flex flex-wrap justify-center gap-3">
                <a
                  href="/demos/restaurant/crm"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white font-semibold text-sm shadow-lg shadow-amber-600/30 transition-all hover:scale-105"
                >
                  <span>Open Restaurant Floor CRM</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
                <button
                  onClick={handleResetModal}
                  className="px-4 py-2.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-stone-300 font-medium text-sm border border-stone-800 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {errorMessage && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-400 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Host & Event Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Event Host / Company Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Goldman Sachs Private Wealth"
                    value={formData.guest_name}
                    onChange={(e) => setFormData({ ...formData, guest_name: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Direct Contact Email *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="e.g. events@corporate.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Direct Phone / Mobile
                  </label>
                  <input
                    type="tel"
                    placeholder="e.g. +91 98765 43210"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Anticipated Guest Count *
                  </label>
                  <input
                    type="number"
                    min={4}
                    max={60}
                    required
                    value={formData.party_size}
                    onChange={(e) => setFormData({ ...formData, party_size: parseInt(e.target.value, 10) || 4 })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
              </div>

              {/* Target Salon & Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Preferred Private Salon *
                  </label>
                  <select
                    value={formData.dining_area}
                    onChange={(e) => setFormData({ ...formData, dining_area: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  >
                    {PRIVATE_DINING_SALONS.map((s) => (
                      <option key={s.id} value={s.name}>
                        {s.name} ({s.capacity})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-300 mb-1">
                    Target Event Date *
                  </label>
                  <input
                    type="date"
                    required
                    min={new Date().toISOString().split("T")[0]}
                    value={formData.reservation_date}
                    onChange={(e) => setFormData({ ...formData, reservation_date: e.target.value })}
                    className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                  />
                </div>
              </div>

              {/* Special Requirements */}
              <div>
                <label className="block text-xs font-semibold text-stone-300 mb-1">
                  Event Specifications & Culinary Requirements
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g. Speeches / AV setup, champagne reception at 7 PM, specific Grand Cru pairings, corporate gifting."
                  value={formData.special_requests}
                  onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                  className="w-full bg-stone-900 border border-stone-800 rounded-xl px-3.5 py-2 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>

              {/* Submit CTA */}
              <div className="pt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-stone-300 font-medium text-xs border border-stone-800 transition-colors"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 hover:from-amber-500 hover:to-amber-500 text-white font-bold text-sm shadow-lg shadow-amber-600/30 transition-all hover:scale-[1.02] disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Submitting Proposal...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 text-amber-200" />
                      <span>Submit Private Dining Dossier</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
